/**
 * Universal Hub catalogue — client-side data + fetch helper (Slice d).
 *
 * The AppLauncher, Home discovery, and any future entry-point strip all
 * pull from a single source: `GET /api/hub`. Because Lucide icons cannot
 * cross the wire, the backend transmits a *string* icon identifier and
 * the frontend resolves it via `ICONS[name]`.
 *
 * If the fetch fails (offline, cold start) we fall back to the *same*
 * catalogue baked in below, so the launcher always paints instantly.
 */
import {
  Heart, HeartHandshake, Briefcase, Home as HouseIcon, Stethoscope, Building2,
  ShoppingBag, Wrench, UtensilsCrossed, GraduationCap, Palmtree, Car, Bike,
  Plane, TrainFront, Bus, Film, Camera, Scissors, Dumbbell, PawPrint, Baby,
  Blocks, PencilRuler, BedDouble, Store, Star, Church, PartyPopper, Landmark,
  Coins, LineChart, Trophy, Code, Package, PlayCircle, Wallet, Hash, IdCard,
  Calculator, Bell, Siren, FolderOpen, Beaker, Globe, ShieldCheck, Users,
  MessageCircle, PlusCircle, LayoutDashboard, Search as SearchIcon, MapPin,
  Sparkles, Inbox as InboxIcon, Bot, Megaphone, Contact2, Rss,
  Store as StoreIcon, Heart as HeartIcon, ShieldAlert, School as SchoolIcon,
  ShoppingCart, PlayCircle as EntertainIcon, Zap, Truck, QrCode, Wand2,
  Briefcase as JobIcon, Home as PropIcon, TrendingUp, Fingerprint, Nfc,
  CheckSquare,
} from "lucide-react";
import { api } from "@/lib/api";

/** Icon-string → React component. Kept in sync with backend routes/hub.py. */
export const HUB_ICONS = {
  Heart, HeartHandshake, Briefcase, HouseIcon, Stethoscope, Building2,
  ShoppingBag, Wrench, UtensilsCrossed, GraduationCap, Palmtree, Car, Bike,
  Plane, TrainFront, Bus, Film, Camera, Scissors, Dumbbell, PawPrint, Baby,
  Blocks, PencilRuler, BedDouble, Store, Star, Church, PartyPopper, Landmark,
  Coins, LineChart, Trophy, Code, Package, PlayCircle, Wallet, Hash, IdCard,
  Calculator, Bell, Siren, FolderOpen, Beaker, Globe, ShieldCheck, Users,
  MessageCircle, PlusCircle, LayoutDashboard, SearchIcon, MapPin, Sparkles,
  InboxIcon, Bot, Megaphone, Contact2, Rss, StoreIcon, HeartIcon, ShieldAlert,
  SchoolIcon, ShoppingCart, EntertainIcon, Zap, Truck, QrCode, Wand2, JobIcon,
  PropIcon, TrendingUp, Fingerprint, Nfc, CheckSquare,
};

/** Resolve an icon name string to a React component; safe fallback = Store. */
export function resolveIcon(name) {
  return HUB_ICONS[name] || Store;
}

/**
 * Fetch the hub catalogue. Returns `{ groups, poi_map }`.
 * On failure returns `null` — caller uses its own fallback (kept as a
 * lightweight paint-instantly path in AppLauncher.jsx).
 */
export async function fetchHub() {
  try {
    const r = await api.get("/hub");
    if (!r?.data?.groups) return null;
    return { groups: r.data.groups, poi_map: r.data.poi_map || {} };
  } catch {
    return null;
  }
}
